import { useEffect, useState } from "react";
import { useLanguage } from "../../../stores/LanguageStore";
import {
  getOtherPrms,
  getStatusCustom,
  getStatusSystem,
} from "../../../api/PRMAPI";
import {
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import Controller from "./controller/Controller";
import Containers from "./containers/Containers";
import { useUIStore } from "../../../stores/UIStore";
import "./PRM.scss";
import { useLeadsStore } from "../../../stores/LeadsStore";
import { defaultStatusColors, setupFilters } from "./utils";
import { useConfigStore } from "../../../stores/ConfigStore";
import LeadsBaseSelector from "./titleSection/LeadsBaseSelector";
import LeadSheet from "./prospect/sheet/LeadSheet";
import { useNavigate, useParams } from "react-router-dom";
import { getFields, getListsQuick } from "../../../api/ContactAPI";
import { getQuickCampaigns } from "../../../api/StatsAPI";
import RequiredActions from "../account/RequiredActions/RequiredActions";
import { IconAddressBook } from "@tabler/icons-react";
import ZapierSettings from "./titleSection/zapier/ZapierSettings";
import { useProfileStore } from "../../../stores/UserStore";
import LeadCreate from "./controller/LeadCreate";
import LeadActions from "./titleSection/LeadActions";
import LeadsSharing from "./titleSection/sharing/LeadsSharing";
import { getSignatures } from "../../../api/ConfigsApi";
import { getMEmails } from "../../../api/ModelsApi";
import { getEmails } from "../../../api/EmailAPI";
import LeadsTrackingMobile from "./mobiles/LeadsTrackingMobile";
import LeadSheetStandalone from "./prospect/sheet/LeadSheetStandalone";
import LeadsImport from "./titleSection/import/LeadsImport";
import LeadsMainTable from "./table/LeadsMainTable";
import PrmLayoutChanger from "./controller/PrmLayoutChanger";

async function init(userId, navigate) {
  // Other requests need for filters
  getListsQuick();
  getQuickCampaigns();
  getFields();
  getSignatures();
  getMEmails();

  getEmails();
  // If on others PRM or self
  if (userId) {
    const setPrms = useLeadsStore.getState().setPrms;
    return getOtherPrms().then((res) => {
      let prms = res.data.prm;
      setPrms(prms);

      let actualPrm = prms?.find((x) => x.id === parseInt(userId));

      if (!actualPrm) {
        return navigate("/leads-tracking");
      }

      let statuses = actualPrm.status;
      let custom_statuses = actualPrm.custom_status;
      let datafields = actualPrm.datafields;

      useLeadsStore.getState().setCustomStatuses(custom_statuses);

      const lang = useLanguage.getState().language;
      let map = {
        opener: lang.components.status_selector.statuses.opener,
        clicker: lang.components.status_selector.statuses.clicker,
        answerer: lang.components.status_selector.statuses.answerer,
        to_call: lang.components.status_selector.statuses.to_call,
        out_of_office: lang.components.status_selector.statuses.out_of_office,
      };

      useLeadsStore.getState().setSystemStatuses(
        statuses.map((x) => ({
          ...x,
          name: map[x.status],
          id: x.status,
        }))
      );

      setupFilters({
        datafields,
        custom_columns: custom_statuses.map((x) => ({
          label: x.name,
          value: x.id.toString(),
        })),
      });

      useLeadsStore.getState().setFields(datafields);
    });
  } else {
    await getStatusSystem().then((response) => {
      const lang = useLanguage.getState().language;
      let map = {
        opener: lang.components.status_selector.statuses.opener,
        clicker: lang.components.status_selector.statuses.clicker,
        answerer: lang.components.status_selector.statuses.answerer,
        to_call: lang.components.status_selector.statuses.to_call,
        out_of_office: lang.components.status_selector.statuses.out_of_office,
      };

      useLeadsStore.getState().setSystemStatuses(
        response.data.status.map((x) => ({
          ...x,
          name: map[x.status],
          id: x.status,
          color: x?.color || defaultStatusColors[x.status] || null,
        }))
      );
    });

    await getStatusCustom().then((response) => {
      const lang = useLanguage.getState().language;
      let map = {
        answerer_positive: lang.prm.status.positive,
        answerer_negative: lang.prm.status.negative,
      };
      useLeadsStore.getState().setCustomStatuses(
        response.data.status.map((x) => ({
          ...x,
          name:
            (x.name === "Answerer negative" ||
              x.name === "Answerer positive") &&
              x?.type_default_status
              ? map[x.type_default_status]
              : x.name,
          color:
            x?.color || defaultStatusColors[x?.type_default_status] || null,
        }))
      );

      let datafields = useConfigStore.getState().datafields;

      setupFilters({
        datafields,
        custom_columns: response.data.status.map((x) => ({
          label: x.name,
          value: x.id.toString(),
        })),
      });

      useLeadsStore.getState().setFields(datafields);
    });
  }
}

const LeadsTracking = ({ isSheet }) => {
  const { userId } = useParams();
  const lang = useLanguage((s) => s.language);
  const setPageTitle = useUIStore((s) => s.setPageTitle);
  const prmLayout = useUIStore((s) => s.prmLayout);
  const setPrmLayout = useUIStore((s) => s.setPrmLayout);
  const [loading, setLoading] = useState(true);
  const profile = useProfileStore((s) => s.profile);

  const navigate = useNavigate();

  useEffect(() => {
    if (loading) {
      init(userId, navigate).then(() => {
        setLoading(false);
        useLeadsStore.getState().setLoadingJobs(true);
      });
    }
  }, [loading, userId]);

  useEffect(() => {
    setLoading(true);
    setPageTitle(lang.prm.browser_tab_title);
  }, [userId]);

  useEffect(() => {
    return () => {
      getOtherPrms();
    };
  }, []);

  if (isSheet) {
    return <LeadSheetStandalone initializing={loading} />;
  }

  if (window.innerWidth < 768) {
    return <LeadsTrackingMobile loading={loading} />;
  }

  return (
    <Stack gap={0} px={64} pb={16}>
      <Paper mt={32} mb={24} component={Group} p={"md"} gap={"md"}>
        <ThemeIcon size={"xl"} radius={"md"} variant="filled" color="primary">
          <IconAddressBook size={32} />
        </ThemeIcon>

        <Text fw={700} fz={24}>
          {lang.leads.title} {lang.statistics.columns.contacted.of}
        </Text>

        <LeadsBaseSelector />

        <LeadsSharing
          disabled={userId ? parseInt(userId) !== profile.id : false}
        />

        <ZapierSettings
          disabled={userId ? parseInt(userId) !== profile.id : false}
        />

        <Divider orientation="vertical" mx={16} />

        <Group gap={"xl"} ml={"auto"}>
          <LeadActions
            disabled={userId ? parseInt(userId) !== profile.id : false}
          />
          <LeadCreate
            disabled={userId ? parseInt(userId) !== profile.id : false}
          />
          <LeadsImport
            disabled={userId ? parseInt(userId) !== profile.id : false}
          />
        </Group>
      </Paper>

      <Stack
        pos={"relative"}
        flex={"1 1 auto"}
        gap={"xs"}
        h={"calc(100vh - 208px)"}
      >
        {loading ? (
          <LoadingOverlay visible style={{ borderRadius: 12 }} />
        ) : (
          prmLayout === "column" ?
            <>
              <Controller />

              <Containers />

              <LeadSheet />
            </>
            :
            <>
              <LeadsMainTable />
              <LeadSheet />
            </>
        )}
      </Stack>

      <RequiredActions />
    </Stack>
  );
};

export default LeadsTracking;
